import React, { useEffect, useState } from "react"
import { useRouter } from "next/router"
import Head from "next/head"
import { useDispatch, useSelector } from "react-redux"
import AemGrid from "@/components/AemGrid"

import { selectCartState } from "@/store/features/cartSlice"
import Script from "next/script"
import { getCartCount, getAriaLabel } from "@/utils/cart"
import { useTranslation } from "next-i18next"

import {
  generateCanonicalUrl,
  getPageLevelScriptsAndStyles,
  getRandom,
  hasComponentType,
} from "@/utils/helper"
import {
  selectGenericState,
  setBrandLogoImg,
  closeMenu,
  openMenu,
  setActiveMainCategory,
  setRegionNav,
  setGlobalStyles,
  setGlobalScripts,
} from "@/store/features/genericSlice"
import { COMPONENT_TYPES, FRAGMENT_TYPES, publicWebsiteUrl } from "@/constants"

const GlobalPageHeader = props => {
  const {
    header = {},
    title = "",
    description = "",
    templateName = "",
    globalScripting = "",
    seoPageScripting = "",
    isOmnichannel = false,
    omniQRParams = {},
    isQrService=false,
    mappingData={}
  } = props

  if (header.pageComponentProperties) {
    Object.assign(header, header.pageComponentProperties)
  }

  const dispatch = useDispatch()
  const router = useRouter()

  const { cartUrl } = useSelector(selectCartState)

  const cartCount = getCartCount()
  const {
    brandLogoImg,
    menu: { isOpen, mainCategoryActive, isRegionOpen },
    pageIsInteractive,
  } = useSelector(selectGenericState)
  const toggleMenu = () => {
    isOpen ? dispatch(closeMenu()) : dispatch(openMenu())
  }
  const { t } = useTranslation("common")
  const ariaLabel = getAriaLabel(t, cartCount)
  const [metas, setMetas] = useState([])
  const [styles, setStyles] = useState([])
  const [scripts, setScripts] = useState([])
  const [links, setLinks] = useState([])
  const [hasGlobalNavigation, setHasGlobalNavigation] = useState(false)

  useEffect(() => {
    const { pageLevelDetails = [] } =
      getPageLevelScriptsAndStyles(globalScripting)

    const { pageLevelDetails: pageLevelScripts = [] } =
      getPageLevelScriptsAndStyles(seoPageScripting)
    setMetas([...pageLevelDetails["metaList"], ...pageLevelScripts["metaList"]])
    setStyles([
      ...pageLevelDetails["stylesList"],
      ...pageLevelScripts["stylesList"],
    ])
    setScripts([
      ...pageLevelDetails["pageScripts"],
      ...pageLevelScripts["pageScripts"],
    ])
    setLinks([...pageLevelDetails["linkList"], ...pageLevelScripts["linkList"]])
  }, [globalScripting, seoPageScripting])

  const redirect = () => {
    router.push(`${cartUrl}`)
  }

  useEffect(() => {
    hasComponentType(header, COMPONENT_TYPES.GLOBAL_NAVIGATION, () =>
      setHasGlobalNavigation(true)
    )
  }, [])

  const eventHandler = {
    store: {
      brandLogoImg: brandLogoImg,
      isOpen,
      mainCategoryActive,
      isRegionOpen,
      cartCount,
      ariaLabel,
      cartUrl,
    },
    callbacks: {
      setBrandLogoImg: path => {
        dispatch(setBrandLogoImg(path))
      },
      closeMenu: () => {
        dispatch(closeMenu())
      },
      setMainCategory: payload => {
        dispatch(setActiveMainCategory(payload))
      },
      toggleMenu: () => {
        toggleMenu()
      },
      setRegionNav: active => {
        dispatch(setRegionNav(active))
      },
      setGlobalScripts: item => {
        dispatch(setGlobalScripts(item))
      },
      setGlobalStyles: item => {
        dispatch(setGlobalStyles(item))
      },
      onClick: () => {
        redirect()
      },
    },
  }
  const [fullUrl, setFullUrl] = useState()
  const [globalFullUrl, setGlobalFullUrl] = useState(null)
  useEffect(() => {
    if (typeof window !== "undefined") {
      let url = window.location.pathname + window.location.search
      setFullUrl(generateCanonicalUrl(url, "", publicWebsiteUrl))
      let globalUrlPath=Object.keys(mappingData).length>0&&mappingData?.filter((value)=>{
        if(url.includes("?")){
          return url.split("?")[0]===value.indiaURL
        }
        else{
          return url===value.indiaURL
        }
      })||[]
      if(globalUrlPath.length>0){
        const urlPath=globalUrlPath[0].globalURL
        setGlobalFullUrl(urlPath)
      }
      if(url==="/"){
        setGlobalFullUrl("https://www.kohler.com/en")
      }
    }
  }, [])

  return (
    <>
      <Head>
        <title>{title}</title>
        {description ? <meta name="description" content={description} /> : null}
        {title ? <meta property="og:title" content={title} /> : null}
        {fullUrl ? <link rel="canonical" href={fullUrl} /> : null}
        {fullUrl ? (
          <link rel="alternate" hrefLang="en-in" href={fullUrl} />
        ) : null}
        {globalFullUrl ? (
          <link rel="alternate" href={globalFullUrl} hreflang="en-US" />
        ) : null}
        {globalFullUrl? (
          <link
            rel="alternate"
            href={globalFullUrl}
            hreflang="x-default"
          />
        ) : null}
        {description ? (
          <meta property="og:description" content={description} />
        ) : null}
        {templateName ? (
          <meta property="og:type" content={templateName} />
        ) : null}
        {metas.map((meta, i) => {
          if (meta.name && meta.content)
            return (
              <meta key={`meta-${i}`} name={meta.name} content={meta.content} />
            )
        })}
        {styles.map((style, i) => {
          if (typeof style === "string")
            return (
              <React.Fragment key={`pagestyle-${i}-${getRandom()}`}>
                <style jsx global>
                  {style}
                </style>
              </React.Fragment>
            )
        })}
        {links.map((link, i) => {
          if (typeof link !== "string")
            return (
              <link key={`pageLinks-${i}`} rel={link.rel} href={link.href} />
            )
        })}
        <link
          rel="icon"
          type="image/x-icon"
          href="/images/favicons/favicon.ico"
        />
      </Head>
      {pageIsInteractive &&
        scripts.map((script, i) => {
          if (script.src)
            return (
              <Script
                key={`headscript-${i}`}
                src={script.src}
                type={script.type}
                integrity={script.integrity}
                id={`headscript-${i}`}
              />
            )
          if (!script.src && script.innerHTML)
            return (
              <Script key={`headscriptHtml-${i}`} id={`headscriptHtml-${i}`} type={script.type}>
                {`${script.innerHTML}`}
              </Script>
            )
        })}

      <AemGrid
        data={header}
        count={0}
        eventHandler={eventHandler}
        fragment={FRAGMENT_TYPES.HEADER}
        hasGlobalNavigation={hasGlobalNavigation}
        isOmnichannel={isOmnichannel}
        omniQRParams={omniQRParams}
        isQrService={isQrService}
      />
    </>
  )
}
export default GlobalPageHeader
